import Quill from 'quill';
import Delta from 'quill-delta';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useMarkdownConverter } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Heading, IconWrapper, QuillInput } from '@treams/treams-ui';

import { GLOBAL_WORKSPACE_ID, prompts } from './constants';

const Circle = styled.div<{ color: string }>`
	width: 26px;
	height: 26px;
	border-radius: 50%;
	background-color: ${(props) =>
		props.color in props.theme.colors ? props.theme.colors[props.color] : props.color};

	display: flex;
	align-items: center;
	justify-content: center;
`;

export default function PromptInstructionsDetails() {
	const { t } = useTranslation();
	const { prompt: slug } = useParams<{ prompt: string }>();
	const prompt = useMemo(() => prompts.find((p) => p.slug === slug), [slug]);

	const instructionsRef = useRef<Quill | undefined>();
	const [setPromptInstruction] = graphql.useSetPromptInstructionMutation();
	const { markdownToQuillDelta, htmlToMarkdown } = useMarkdownConverter();
	const [content, setContent] = useState<Delta | undefined>();

	const promptInstructionQuery = graphql.usePromptInstructionQuery({
		skip: !prompt,
		variables: {
			workspaceID: GLOBAL_WORKSPACE_ID,
			promptInstructionID: prompt?.id ?? graphql.Prompt.All,
		},
	});

	useEffect(() => {
		const instruction = promptInstructionQuery.data?.promptInstruction;
		const delta = markdownToQuillDelta(instruction?.instructions ?? '');
		setContent(delta);
		instructionsRef.current?.setContents(delta, 'api');
	}, [promptInstructionQuery.data?.promptInstruction, markdownToQuillDelta]);

	const saveInstructions = useCallback(
		async (ref: MutableRefObject<Quill | undefined>) => {
			if (!ref.current || !prompt) return;

			const html = ref.current.getSemanticHTML();
			const instructions = htmlToMarkdown(html);

			await setPromptInstruction({
				variables: {
					input: {
						workspaceID: GLOBAL_WORKSPACE_ID,
						promptInstructionID: prompt.id,
						instructions,
					},
				},
			});
		},
		[setPromptInstruction, htmlToMarkdown, prompt],
	);

	return (
		<Box flexDirection="column">
			{prompt && (
				<Flex alignItems="center">
					<Circle color={prompt.color}>
						<IconWrapper size={16} color="white">
							{prompt.icon}
						</IconWrapper>
					</Circle>
					<Heading ml={2}>{prompt.title}</Heading>
				</Flex>
			)}
			<Box mt={3}>
				{content && (
					<QuillInput
						onQuill={(quillEditor) => {
							instructionsRef.current = quillEditor;
						}}
						value={content}
						onChange={setContent}
						hideLink
						placeholder={t('settings.ai-workbench.enter-instructions')}
					/>
				)}
			</Box>
			<Button title="Save" mt={2} onClick={() => saveInstructions(instructionsRef)} />
		</Box>
	);
}
